import type { MarketplaceSearchResult } from "@/types/api/marketplace";

export type MarketplaceSearchPanelStatus =
  | "browse"
  | "typing"
  | "loading"
  | "results"
  | "related"
  | "empty";

function haystack(item: MarketplaceSearchResult): string {
  return `${item.title} ${item.subtitle}`.toLowerCase();
}

function matchesQuery(item: MarketplaceSearchResult, query: string): boolean {
  return haystack(item).includes(query);
}

/** Loose match: any query token (2+ chars) appears in title/subtitle */
function matchesRelated(item: MarketplaceSearchResult, query: string): boolean {
  const hay = haystack(item);
  const tokens = query.split(/\s+/).filter((t) => t.length >= 2);
  if (tokens.length === 0) {
    return query.length >= 3 && hay.includes(query.slice(0, 3));
  }
  return tokens.some((token) => hay.includes(token));
}

export function partitionMarketplaceSuggestions(
  suggestions: MarketplaceSearchResult[],
  query: string
): { exact: MarketplaceSearchResult[]; related: MarketplaceSearchResult[] } {
  const q = query.trim().toLowerCase();
  if (!q) {
    return { exact: suggestions, related: [] };
  }

  const exact = suggestions.filter((item) => matchesQuery(item, q));
  if (exact.length > 0) {
    return { exact, related: [] };
  }

  const related = suggestions.filter((item) => matchesRelated(item, q));

  if (related.length > 0) {
    return { exact: [], related };
  }

  return { exact: [], related: suggestions.slice(0, 4) };
}

export function resolveMarketplaceSearchPanelStatus({
  searchQuery,
  debouncedSearch,
  isFetching,
  exactCount,
  relatedCount,
  isFocused,
}: {
  searchQuery: string;
  debouncedSearch: string;
  isFetching: boolean;
  exactCount: number;
  relatedCount: number;
  isFocused: boolean;
}): MarketplaceSearchPanelStatus {
  if (!isFocused) return "browse";

  const trimmed = searchQuery.trim();
  const debouncedTrimmed = debouncedSearch.trim();
  const isTyping =
    trimmed.length > 0 &&
    trimmed.toLowerCase() !== debouncedTrimmed.toLowerCase();

  if (trimmed.length === 0) return "browse";
  if (isTyping || (isFetching && debouncedTrimmed.length === 0)) {
    return "typing";
  }
  if (isFetching) return "loading";
  if (exactCount > 0) return "results";
  if (relatedCount > 0) return "related";
  return "empty";
}
